import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExcellenceWeekContent from './ExcellenceWeekContent';
import './panels.css';

function ExcellenceWeekPanel({ week, user, apiUrl, onStartPractice }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', body: '' });
  const [saving, setSaving] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const fetchContent = () => {
    setLoading(true);
    setEditing(false);
    setUnlockCode('');
    setUnlockError('');
    return axios
      .get(`${apiUrl}/content/excellence/${week}`)
      .then((r) => {
        setData(r.data);
        if (!r.data.locked) setForm({ title: r.data.title, body: r.data.body });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week, apiUrl]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${apiUrl}/content/excellence/${week}`, form);
      setData((prev) => ({ ...prev, ...res.data }));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!unlockCode.trim()) return;
    setUnlocking(true);
    setUnlockError('');
    try {
      const res = await axios.post(`${apiUrl}/content/excellence/${week}/unlock`, { code: unlockCode });
      if (res.data.unlocked) {
        fetchContent();
      } else {
        setUnlockError('That code is not correct. Double-check with your tutor.');
      }
    } catch (err) {
      setUnlockError(err.response?.data?.error || 'That code is not correct. Double-check with your tutor.');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>Loading...
      </div>
    );
  }

  if (data?.locked && data.reason === 'code') {
    return (
      <div className="panel">
        <div className="locked-placeholder">
          <div className="locked-icon">🔒</div>
          <h2>Week {week}</h2>
          <p>This week unlocks with a code from your tutor. Ask them for the code to Week {week}, then enter it below.</p>
          <form className="unlock-form" onSubmit={handleUnlock}>
            <input
              type="text"
              className="unlock-code-input"
              placeholder="Enter code"
              value={unlockCode}
              onChange={(e) => setUnlockCode(e.target.value)}
              disabled={unlocking}
            />
            <button type="submit" className="btn btn-primary" disabled={unlocking || !unlockCode.trim()}>
              {unlocking ? 'Checking...' : 'Unlock'}
            </button>
          </form>
          {unlockError && <p className="unlock-error">{unlockError}</p>}
        </div>
      </div>
    );
  }

  if (data?.locked) {
    return (
      <div className="panel">
        <div className="locked-placeholder">
          <div className="locked-icon">🔒</div>
          <h2>Week {week}</h2>
          <p>This week is part of the UCAT Excellence Program. Contact your tutor or admin to upgrade your access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      {editing ? (
        <div className="content-edit-form">
          <input
            type="text"
            className="content-title-input"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder={`Week ${week}`}
          />
          <textarea
            className="content-body-textarea"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            rows={16}
            placeholder="Add this week's content... (Markdown supported: # Heading, **bold**, - bullet lists)"
          />
          <div className="content-edit-actions">
            <button className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      ) : (
        <>
          <div className="panel-header-row">
            <h2>{data?.title || `Week ${week}`}</h2>
            {user.role === 'admin' && (
              <button className="btn btn-secondary" onClick={() => setEditing(true)}>Edit</button>
            )}
          </div>
          {(user.role === 'tutor' || user.role === 'admin') && data?.nextWeekCode && (
            <div className="next-week-code-box">
              <span>🔑</span>
              <span>
                <strong>Code to unlock Week {Number(week) + 1}:</strong>{' '}
                <span className="next-week-code-value">{data.nextWeekCode}</span>
                {' '}— give this to students once they're ready to move on.
              </span>
            </div>
          )}
          {data?.body ? (
            <ExcellenceWeekContent body={data.body} onStartPractice={onStartPractice} />
          ) : (
            <p className="content-empty">Content for this week hasn't been added yet.</p>
          )}
        </>
      )}
    </div>
  );
}

export default ExcellenceWeekPanel;
